import { z } from 'zod';
import { agencySettingsSchema, jobProfileSchema, requirementPolicySchema, requirementRuleSchema, requirementSetSchema, tagProfileSchema, } from '../requirements/index.js';
import { buildPersonChecklist } from '../readiness/build.js';
import { personSchema } from '../people/person.js';
import { requirementSeeds } from './requirements.seed.js';
import { jobProfileSeeds, requirementSetSeeds, tagProfileSeeds } from './profiles.seed.js';
export * from './types.js';
export * from './steps.seed.js';
export * from './step-backed.js';
export * from './seeder.js';
export * from './settings.seed.js';
export { requirementSeeds } from './requirements.seed.js';
export { jobProfileSeeds, requirementSetSeeds, tagProfileSeeds } from './profiles.seed.js';
/**
 * The seeded catalog: the shared source of truth every surface reads (ADR-006).
 *
 * Seeds are PARSED through the real schemas here, never cast. That is what makes the seed
 * files data rather than a second set of type declarations — a row that cannot be a valid
 * RequirementRule fails at import, which the test suite turns into a red build.
 *
 * Defaults live in ONE place, right here, so "what does an unspecified row do?" has a
 * single answer.
 */
/** Decision 2, and the only place the operating states live. */
export const agencySettings = agencySettingsSchema.parse({
    id: 'agency-settings',
    docType: 'agency-settings',
    jurisdictions: ['VA'],
    timeZone: 'America/New_York',
    // Decision 1. Recorded as data so a rule can state which accreditor it derives from,
    // rather than "CHAP only" being implemented by silent omission.
    accreditors: ['CHAP'],
});
function toResolvedRule(seed) {
    const rule = requirementRuleSchema.parse({
        id: `requirement:${seed.key}`,
        docType: 'requirement-rule',
        key: seed.key,
        currentVersion: seed.version ?? 1,
        presentation: { label: seed.label },
        metadata: {
            legalName: seed.legalName ?? '',
            citations: seed.citations ?? [],
            fileCategory: seed.fileCategory,
            catalogRow: seed.catalogRow ?? '',
        },
    });
    const gates = seed.gates ?? [];
    const isAgency = seed.owner === 'agency';
    const policy = requirementPolicySchema.parse({
        compliance: seed.compliance,
        kind: seed.kind,
        phase: seed.phase,
        owner: seed.owner,
        expiry: seed.expiry,
        blocking: {
            gates,
            waivable: seed.waivable ?? false,
        },
        visibility: {
            // Decision 6: agency rows stay VISIBLE on the person's checklist. They are shown
            // and simply never counted or gated — visibility and blocking are separate
            // questions, so nothing is hidden to keep a progress bar tidy.
            surfaces: seed.surfaces ?? ['person-checklist', 'hr-checklist', 'reports', 'audit-export'],
            countsTowardProgress: seed.countsTowardProgress ?? !isAgency,
            restrictedToRole: null,
        },
        trigger: seed.trigger ?? { mode: 'always' },
        satisfaction: { mode: 'all-steps', derivedFrom: 'step-instances' },
        prerequisites: seed.prerequisites ?? [],
        jurisdictions: seed.jurisdictions ?? [],
    });
    return { rule, policy };
}
/**
 * Set members get their `order` from the set's band, by position. The band is what keeps
 * two composed sets from interleaving — see profiles.seed.ts.
 */
function toRequirementSet(seed) {
    const [start] = seed.orderBand;
    return requirementSetSchema.parse({
        id: `set:${seed.key}`,
        docType: 'requirement-set',
        key: seed.key,
        label: seed.label,
        members: seed.members.map((requirementKey, index) => ({ requirementKey, order: start + index })),
    });
}
/** Profile-level additions sort after every set band. */
const PROFILE_ADD_ORDER_BASE = 900;
function toJobProfile(seed, index) {
    return jobProfileSchema.parse({
        id: `job:${seed.key}`,
        docType: 'job-profile',
        key: seed.key,
        label: seed.label,
        directCare: seed.directCare,
        displayOrder: index,
        composition: {
            includes: seed.includes,
            adds: seed.adds.map((requirementKey, i) => ({
                requirementKey,
                order: PROFILE_ADD_ORDER_BASE + i,
            })),
            removes: seed.removes ?? [],
        },
    });
}
const TAG_ADD_ORDER_BASE = 950;
function toTagProfile(seed) {
    return tagProfileSchema.parse({
        id: `tag:${seed.tag}`,
        docType: 'tag-profile',
        tag: seed.tag,
        label: seed.label,
        activeWhen: null,
        composition: {
            includes: seed.includes,
            adds: seed.adds.map((requirementKey, i) => ({ requirementKey, order: TAG_ADD_ORDER_BASE + i })),
            removes: [],
        },
    });
}
export const seedCatalog = {
    requirements: requirementSeeds.map(toResolvedRule),
    sets: requirementSetSeeds.map(toRequirementSet),
    jobProfiles: jobProfileSeeds.map(toJobProfile),
    tagProfiles: tagProfileSeeds.map(toTagProfile),
    agencySettings,
};
/**
 * Whether a requirement is tracked by the expiry date on the document itself.
 *
 * This is the lookup `approveInstance` takes, built once here because the catalog layer is
 * where requirement policy lives — the step engine below it must not reach up for this.
 * Every approval path passes THIS value, so there is one answer to the question rather than
 * a copy of it per handler.
 *
 * An unknown key answers false. A credential minted against a requirement that does not
 * exist is its own problem, guarded at seed time by `check:requirement-keys`, and blocking
 * an approval on it here would refuse a real submission for an unrelated reason.
 */
export const seedRequirementNeedsCredentialExpiry = (requirementKey) => seedCatalog.requirements.find((r) => r.rule.key === requirementKey)?.policy.expiry.mode ===
    'credential-date';
/**
 * The catalog in the shape `resolveChecklist` consumes.
 *
 * This is the ONLY supported way for a feature to reach requirement configuration. Feature
 * code asks this; it never names a row. `scripts/check-requirement-keys.mjs` enforces that.
 */
/**
 * Cached for the default catalog: it is built from static seed data, so rebuilding ~200
 * documents into maps on every request bought nothing and widened the window where a
 * person's page has nothing to show.
 */
let defaultCatalog = null;
export function toRequirementCatalog(catalog = seedCatalog) {
    if (catalog === seedCatalog && defaultCatalog !== null)
        return defaultCatalog;
    const built = buildRequirementCatalog(catalog);
    if (catalog === seedCatalog)
        defaultCatalog = built;
    return built;
}
function buildRequirementCatalog(catalog) {
    return {
        rules: new Map(catalog.requirements.map((r) => [r.rule.key, r])),
        sets: new Map(catalog.sets.map((s) => [s.key, s])),
        jobProfiles: new Map(catalog.jobProfiles.map((p) => [p.key, p])),
        tagProfiles: new Map(catalog.tagProfiles.map((t) => [t.tag, t])),
        jurisdictions: catalog.agencySettings.jurisdictions,
    };
}
/**
 * Rows the catalog carries that are deliberately NOT seeded, kept so the omission is a
 * recorded decision rather than something that looks like an oversight later.
 */
export const droppedFromCatalog = [
    { key: 'annual-competency-evaluation-achc', why: 'Decision 1 — CHAP only. The CHAP-side ongoing-competency row is seeded instead (catalog Q6).' },
    { key: 'performance-appraisal-annual-achc', why: 'Decision 1 — CHAP only. The CHAP-side appraisal row is seeded instead (catalog Q7).' },
    { key: 'profile:swa', why: 'Decision 3 — no separate Social Work Assistant role in the MVP list.' },
    { key: 'profile:students', why: 'Decision 3 — student tracking is out of scope for the MVP.' },
];
/**
 * Rows whose trigger cannot be expressed yet, and exactly what is missing.
 *
 * Each needs a fact the person model does not hold — mostly a patient/case model that
 * Phase 1 has no concept of. They are seeded as `always` and carried as agency-owned where
 * they describe a duty performed on others, so nobody is blocked and nobody's progress bar
 * moves. Recorded here so the gap is visible rather than inferred from a missing feature.
 */
export const unmodelledTriggers = [
    { key: 'hha-supervisory-visit-14-day-skilled', needs: 'a patient/case model — "where the patient also receives skilled services"' },
    { key: 'hha-supervisory-visit-60-day-nonskilled', needs: 'a patient/case model — "where the patient receives no skilled service"' },
    { key: 'hha-semiannual-direct-observation-nonskilled', needs: 'a patient/case model' },
    { key: 'contract-hha-supervision-and-competency', needs: 'a profile-membership trigger clause for contracted aide services' },
    { key: 'student-supervision-by-agency-staff', needs: 'a student-placement record; students are out of MVP scope (decision 3)' },
];
/**
 * The picker options a screen needs — job profiles and tags, as label/value pairs.
 *
 * Served over the API rather than imported into the browser bundle: the full catalog is
 * ~126 rules with citations and evidence text, and a form that only needs names should not
 * ship all of it. It is still the SAME seeded data, so a renamed profile moves the picker
 * without anyone editing a screen.
 */
export const catalogOptionsSchema = z.object({
    jobProfiles: z.array(z.object({
        key: z.string(),
        label: z.string(),
        directCare: z.boolean(),
        /**
         * How many checklist items this role alone brings (#279). Resolved through the SAME
         * `resolveChecklist` the person's own checklist is built from, for a person holding only
         * this role — so the tile and the checklist that follows cannot disagree. Tags add to it,
         * which is what the form's "Also applies" line says.
         */
        items: z.number().int().nonnegative(),
    })),
    tags: z.array(z.object({ key: z.string(), label: z.string() })),
});
/**
 * The items a role brings on its own, for the Add candidate tiles (#279).
 *
 * Counted by building the REAL checklist — `buildPersonChecklist` at the same gate and surface
 * the person's own checklist screen uses — for a person holding only this role and nothing else.
 * Counting resolved rules instead gives a bigger number than HR ever sees: not every scoped row
 * is shown on a surface, and a tile promising 89 items beside a checklist of 76 is a worse lie
 * than no number at all.
 *
 * Tags and answered attributes add to it, which is what the form's "Also applies" line says.
 */
function roleItemCount(profileKey, catalog) {
    const person = personSchema.parse({
        id: COUNTING_PERSON_ID, personId: COUNTING_PERSON_ID, docType: 'person', lifecycle: 'candidate',
        name: { givenName: 'Role', familyName: 'Count' }, contact: { email: 'role-count@example.invalid' },
        jobProfileKeys: [profileKey], tags: [], attributes: {},
        createdAt: COUNTING_INSTANT, updatedAt: COUNTING_INSTANT, createdBy: 'catalog', updatedBy: 'catalog',
    });
    return buildPersonChecklist({
        person, catalog: toRequirementCatalog(catalog), gate: 'first-assignment', surface: 'hr-checklist', now: COUNTING_INSTANT,
    }).items.length;
}
/** Fixed, so the count is a pure function of the catalog and never moves with the clock. */
const COUNTING_PERSON_ID = '00000000-0000-4000-8000-000000000000';
const COUNTING_INSTANT = '2026-01-01T00:00:00.000Z';
export function catalogOptions(catalog = seedCatalog) {
    return {
        jobProfiles: catalog.jobProfiles
            .filter((p) => p.retiredAt === null)
            .map((p) => ({ key: p.key, label: p.label, directCare: p.directCare, items: roleItemCount(p.key, catalog) })),
        tags: catalog.tagProfiles
            .filter((t) => t.retiredAt === null)
            .map((t) => ({ key: t.tag, label: t.label })),
    };
}
/**
 * A job profile's label by its key, with no request: the same words `catalogOptions` serves, read from the same seeded catalog.
 * A retired profile still has its label, since a person can hold one. Null for a key the catalog does not hold. The Roster's
 * table shows job titles with it (#798) without reading the catalog while the Table view loads (#699).
 */
export function jobProfileLabel(key, catalog = seedCatalog) {
    return catalog.jobProfiles.find((p) => p.key === key)?.label ?? null;
}
//# sourceMappingURL=index.js.map